// Precondition: None
// Postcondition: Custom element for chat text input with card layout and submit button
// Invariants: Dispatches 'chat-submit' event when message is submitted (unless redirect-url is set)

import { icon } from "../../../shared/icons.js";

if (!customElements.get("ft-chat-input")) {
  type InputStatus = "ready" | "submitted" | "streaming" | "error";

  class FtChatInput extends HTMLElement {
    private textarea: HTMLTextAreaElement | null = null;
    private submitBtn: HTMLButtonElement | null = null;
    private _status: InputStatus = "ready";

    constructor() {
      super();
      this.attachShadow({ mode: "open" });
    }

    static get observedAttributes(): string[] {
      // Note: redirect-url, cik, quarter are read-only at submit time, not observed
      return ["status", "disabled", "placeholder", "hide-disclaimer"];
    }

    get status(): InputStatus {
      return this._status;
    }

    set status(value: InputStatus) {
      this._status = value;
      this.updateSubmitButton();
    }

    get value(): string {
      return this.textarea?.value.trim() || "";
    }

    set value(val: string) {
      if (this.textarea) {
        this.textarea.value = val;
        this.autoResize();
      }
    }

    connectedCallback(): void {
      this.render();
      this.setupReferences();
      this.setupEventListeners();
    }

    attributeChangedCallback(
      name: string,
      oldValue: string | null,
      newValue: string | null,
    ): void {
      if (name === "status" && newValue) {
        this._status = newValue as InputStatus;
        this.updateSubmitButton();
      }
      if (name === "disabled") {
        const isDisabled = newValue !== null;
        if (this.textarea) this.textarea.disabled = isDisabled;
        if (this.submitBtn) this.submitBtn.disabled = isDisabled;
      }
      if (name === "placeholder" && this.textarea && newValue) {
        this.textarea.placeholder = newValue;
      }
    }

    private render(): void {
      const style = document.createElement("style");
      style.textContent = this.getStyles();

      const template = document.createElement("template");
      template.innerHTML = this.getTemplate();

      if (this.shadowRoot) {
        this.shadowRoot.appendChild(style);
        this.shadowRoot.appendChild(template.content.cloneNode(true));
      }
    }

    private getStyles(): string {
      return `
        :host {
          display: block;
          margin-bottom: var(--space-md, 24px);
        }

        .input-card {
          background: var(--input-bg, #ffffff);
          border: 1px solid var(--card-border, #E2E4E8);
          border-radius: var(--radius-lg, 16px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
          padding: 12px 16px;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }

        .input-card:focus-within {
          border-color: var(--primary, #E0524D);
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary, #E0524D) 10%, transparent);
        }

        .input-main {
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }

        textarea {
          flex: 1;
          min-height: 24px;
          max-height: 200px;
          padding: 0;
          border: none;
          background: transparent;
          font: 400 15px/1.5 var(--font-sans, Inter, system-ui, sans-serif);
          color: var(--text, #1C1C1E);
          resize: none;
          outline: none;
        }

        textarea::placeholder {
          color: var(--input-placeholder, #999999);
        }

        .submit-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: var(--height-md, 40px);
          height: var(--height-md, 40px);
          padding: 0;
          border: none;
          background: color-mix(in srgb, var(--primary, #E0524D) 15%, white);
          border-radius: var(--radius-md, 12px);
          cursor: pointer;
          color: var(--primary, #E0524D);
          transition: all 0.15s ease;
          flex-shrink: 0;
        }

        .submit-btn:hover:not(:disabled) {
          background: var(--primary, #E0524D);
          color: var(--primary-text, #ffffff);
        }

        .submit-btn:active:not(:disabled) {
          transform: scale(0.95);
        }

        .submit-btn:disabled {
          background: var(--surface, #f0f0f0);
          color: var(--muted, #555555);
          cursor: not-allowed;
          opacity: 0.5;
        }

        .submit-btn.streaming {
          background: var(--surface, #f0f0f0);
        }

        .submit-btn svg {
          width: 20px;
          height: 20px;
        }

        .loader {
          display: none;
          width: 20px;
          height: 20px;
        }

        .submit-btn.streaming .loader {
          display: block;
        }

        .submit-btn.streaming .send-icon {
          display: none;
        }

        .loader::after {
          content: "";
          display: block;
          width: 16px;
          height: 16px;
          margin: 2px;
          border: 2px solid currentColor;
          border-color: currentColor transparent currentColor transparent;
          border-radius: 50%;
          animation: spin 1.2s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .disclaimer {
          margin-top: 8px;
          text-align: center;
          font: 400 12px/1.4 var(--font-sans, Inter, system-ui, sans-serif);
          color: var(--muted, #555555);
        }

        @media (prefers-color-scheme: dark) {
          .input-card {
            background: var(--surface, #14161a);
            border-color: var(--card-border, #24262c);
          }

          textarea {
            color: var(--text, #ffffff);
          }

          textarea::placeholder {
            color: var(--input-placeholder, #8a8a8a);
          }

          .submit-btn {
            background: color-mix(in srgb, var(--primary, #E0524D) 20%, transparent);
          }

          .submit-btn:disabled {
            background: var(--surface, #14161a);
            color: var(--muted, #6b7280);
          }

          .disclaimer {
            color: var(--muted, #a3a3a3);
          }
        }
      `;
    }

    private getTemplate(): string {
      const placeholder = this.getAttribute("placeholder") || "How can I help you today?";
      const hideDisclaimer = this.hasAttribute("hide-disclaimer");
      const disclaimer = hideDisclaimer
        ? ""
        : '<p class="disclaimer">AI responses may contain errors. Verify against official SEC filings.</p>';

      return `
        <div class="input-card">
          <div class="input-main">
            <textarea
              placeholder="${placeholder}"
              rows="1"
              aria-label="Message input"
            ></textarea>
            <button class="submit-btn" type="button" aria-label="Send message" title="Send message">
              <span class="send-icon">${icon("arrow-up", 20)}</span>
              <span class="loader"></span>
            </button>
          </div>
        </div>
        ${disclaimer}
      `;
    }

    private setupReferences(): void {
      this.textarea = this.shadowRoot?.querySelector("textarea") ?? null;
      this.submitBtn = this.shadowRoot?.querySelector(".submit-btn") ?? null;
    }

    private setupEventListeners(): void {
      // Auto-resize textarea
      this.textarea?.addEventListener("input", () => {
        this.autoResize();
        this.updateSubmitButton();
      });

      // Submit on Enter (Shift+Enter for newline)
      this.textarea?.addEventListener("keydown", (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          this.submit();
        }
      });

      // Submit button click
      this.submitBtn?.addEventListener("click", () => this.submit());
    }

    private autoResize(): void {
      if (!this.textarea) return;
      this.textarea.style.height = "auto";
      this.textarea.style.height = `${Math.min(this.textarea.scrollHeight, 200)}px`;
    }

    private updateSubmitButton(): void {
      if (!this.submitBtn) return;

      const hasText = this.value.length > 0;
      const isStreaming = this._status === "streaming";
      const isSubmitted = this._status === "submitted";

      this.submitBtn.disabled = !hasText || isSubmitted || isStreaming;
      this.submitBtn.classList.toggle("streaming", isStreaming);
    }

    private submit(): void {
      const text = this.value;
      if (!text || this._status === "streaming" || this._status === "submitted")
        return;

      // Handle redirect mode (replaces ft-chat-prompt functionality)
      const redirectUrl = this.getAttribute("redirect-url");
      if (redirectUrl) {
        const params = new URLSearchParams({ q: text });
        const cik = this.getAttribute("cik");
        const quarter = this.getAttribute("quarter");
        if (cik) params.set("cik", cik);
        if (quarter) params.set("quarter", quarter);
        window.location.href = `${redirectUrl}?${params}`;
        return;
      }

      // Standard event mode
      this.dispatchEvent(
        new CustomEvent("chat-submit", {
          detail: { text },
          bubbles: true,
          composed: true,
        }),
      );
    }

    // Public API
    focus(): void {
      this.textarea?.focus();
    }

    clear(): void {
      if (this.textarea) {
        this.textarea.value = "";
        this.autoResize();
        this.updateSubmitButton();
      }
    }
  }

  customElements.define("ft-chat-input", FtChatInput);
}
